import { ReactComponent as ClientIcon } from "assets/images/icons/img_device_client.svg";
import { ReactComponent as WiredIcon } from "assets/images/icons/btn_wired.svg";
import { ReactComponent as WirelessIcon } from "assets/images/icons/btn_wireless.svg";
import { ReactComponent as DescriptionIcon } from "assets/images/icons/icn_table_cell_Message_white.svg";

import { useState, useEffect, useMemo } from 'react';
import { cloneDeep, isNil } from 'lodash';

import { useTheme } from '@mui/material/styles';
import { Box, Typography, TextField, Tooltip } from '@mui/material';

import useDataSourceManager from "hooks/useDataSourceManager";

import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import TabsField from 'components/Tab';
import StatusTag from "components/StatusTag";
import Loading from './loading';
import GeneralContent from './content/general';
import SettingsContent from './content/settings';
import StatisticContent from './content/statistic';

import { CLIENT_STATUS_TAG, CLIENT_TAG_LABEL, uuids } from './util/const';
import { getTabData, initClientData } from './util/func';
import Api from "./util/api";

const headerIconStyle = {
  width: '32px',
};

const headerUptimeStyle = {
  marginRight: '8px',
};

const ClientContent = ({
  auth,
  selectedClientId = null,
  selectedSiteId = null,
  onDrawerApplyCallback = () => { },
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth, selectedSiteId);

  const showEditGeneral = useMemo(() => auth.permissions !== "Viewer", [auth]);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [tabData, setTabData] = useState([]);
  const [tabValue, setTabValue] = useState('general');
  const [clonedSelectedClient, setClonedSelectedClient] = useState({});
  const [clonedOriginalSelectedClient, setOriginalClonedSelectedClient] = useState({});
  const [isWired, setIsWired] = useState(true);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  // Method
  const handleChangeDescription = (newDescription) => {
    setClonedSelectedClient({
      ...clonedSelectedClient,
      description: newDescription,
    });
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setClonedSelectedClient(prevClient =>{
      return {
        ...prevClient,
        description: clonedOriginalSelectedClient.description
      }
    });
  }

  const handleSaveDescription = () => {
    setIsLoading(true);
    const { apiKey, params } = api.getUpdateRequestParams(clonedSelectedClient.clientId);
    const payload = {
      id: clonedSelectedClient.clientId,
      name: clonedSelectedClient.clientName,
      description: clonedSelectedClient.description,
    };

    sendRequest(
      apiKey,
      uuids.updateClient,
      params,
      payload,
    );
  };

  // Side effect
  useEffect(() => {
    if (clonedSelectedClient) {
      const isWiredClient = !clonedSelectedClient.ssid;
      setIsWired(isWiredClient);
      const cloneDefaultTabData = cloneDeep(getTabData(isWiredClient));
      setTabData(cloneDefaultTabData);

      if (!isWiredClient) {
        setTabData(cloneDefaultTabData);
        if (tabValue === 'statistics') {
          setTabValue('general');
        }
      }
    }
  }, [clonedSelectedClient]);

  const fetchTableData = () => {
    if (!selectedClientId) return;

    const { apiKey, params } = api.getSelectedClientRequestParams(selectedClientId);

    sendRequest(
      apiKey,
      uuids.selectedClient,
      params
    );
  }

  useEffect(() => {
    fetchTableData();
  }, [selectedClientId]);

  // Update
  useEffect(() => {
    const responseData = getData(uuids.updateClient);

    if (responseData) {
      setIsLoading(false);
      if (responseData.hasOwnProperty("code") && responseData?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: responseData.error?.message || 'Failed to update client settings.',
        });
      } else {
        onDrawerApplyCallback && onDrawerApplyCallback();
        setOriginalClonedSelectedClient(clonedSelectedClient);
      }
    }
  }, [dataMap]);

  // Read
  useEffect(() => {
    const response = getData(uuids.selectedClient);
    const responseData = response?.data?.[0] ?? null;

    if (responseData) {
      setIsLoading(false);

      if (responseData.hasOwnProperty("code") && responseData?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: responseData.error?.message || 'Failed to get client.',
        });
      } else {
        const initClient = initClientData({responseData});
        setClonedSelectedClient(initClient);
        setOriginalClonedSelectedClient(initClient);
      }
    }
  }, [dataMap?.[uuids.selectedClient]]);

  if (!clonedSelectedClient) {
    return null;
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {isLoading && <Loading />}

        <Box sx={{ display: 'flex', paddingBottom: 2, px: 6 }}>
          <ClientIcon />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginLeft: 1,
              overflowWrap: 'anywhere',
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
              }}
            >
              {clonedSelectedClient.formattedName || '-'}
            </Typography>
            <Typography
              sx={{
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[3],
                fontWeight: theme.typography.customFontWeight[0],
              }}
            >
              {clonedSelectedClient.clientId}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
              }}
            >
              <StatusTag
                status={clonedSelectedClient.status}
                statusMapping={CLIENT_STATUS_TAG}
                labelMapping={CLIENT_TAG_LABEL}
              />
              {/* 目前API尚未實作description部分, 暫時隱藏 */}
              {/* <Tooltip
                placement="bottom"
                arrow
                interactive
                enterDelay={200}
                leaveDelay={200}
                title={
                  <Box sx={{ p: 1, width: 260 }}>
                    <Box
                      sx={{
                        fontSize: theme.typography.customFontSize[1],
                        fontWeight: theme.typography.customFontWeight[3],
                        color: theme.palette.primary.white,
                        mb: 1.2,
                      }}
                    >
                      Descriptions
                    </Box>
                    {!isEditingDescription ? (
                      <>
                        <Box
                          sx={{
                            fontSize: theme.typography.customFontSize[1],
                            fontWeight: theme.typography.customFontWeight[0],
                            color: theme.palette.primary.white,
                            mb: 1,
                          }}
                        >
                          {clonedSelectedClient.description || "No description."}
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }} >
                          <SolidButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsEditingDescription(true);
                            }}
                            width="100px"
                            height="32px"
                          >
                            Edit
                          </SolidButton>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ width: "100%" }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={clonedSelectedClient.description || ""}
                            onChange={(e) => handleChangeDescription(e.target.value)}
                            inputProps={{ maxLength: 200 }}
                            autoFocus
                            sx={{
                              "& .MuiInputBase-root": {
                                backgroundColor: theme.palette.primary.white,
                                borderRadius: theme.shape.customBorderRadius[1],
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="theme.palette.primary.w"
                            >
                              { clonedSelectedClient?.description?.length || 0 } / 200
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <OutlineButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEditDescription();
                              }}
                              sx={{ marginRight: "20px" }}
                              width="100px"
                              height="32px"
                            >
                              Cancel
                            </OutlineButton>
                            <SolidButton
                              onClick={handleSaveDescription}
                              width="100px"
                              height="32px"
                            >
                              Save
                            </SolidButton>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                }
              >
                <Box
                  sx={{
                    width: 36,
                    height: 28,
                    borderRadius: theme.shape.customBorderRadius[0],
                    backgroundColor: theme.palette.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.mainHover,
                    },
                  }}
                >
                  <DescriptionIcon />
                </Box>
              </Tooltip> */}
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  border: `1.5px solid ${theme.palette.primary.main}`,
                  padding: "4px 12px",
                  height: "28px",
                  fontSize: theme.typography.customFontSize[0],
                  borderRadius: theme.shape.customBorderRadius[7],
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  display: "flex",
                }}
              >
                {isWired ? <WiredIcon style={headerIconStyle} /> : <WirelessIcon style={headerIconStyle} />}
                {
                  !isWired && clonedSelectedClient.status === 'online' && (
                    <Box style={headerUptimeStyle}>
                      {clonedSelectedClient.upTime}
                    </Box>
                  )
                }
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <TabsField
            tabsStyles={{
              '& .MuiButtonBase-root': {
                margin: '0 20px',
              },
            }}
            tabVal={tabValue}
            handleTabsChange={(e, newValue) => setTabValue(newValue)}
            tabData={tabData}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {
          tabValue === 'general' && (
            <GeneralContent
              {...{
                isWired,
                selectedClient: clonedSelectedClient,
                selectedSiteId,
              }}
            />
          )
        }
        {
          tabValue === 'settings' && (
            <SettingsContent
              {...{
                auth,
                selectedClient: clonedSelectedClient,
                selectedSiteId,
                onSettingsApplyCallback: () => {
                  fetchTableData();
                  onDrawerApplyCallback();
                },
                showEditGeneral,
                setIsLoading,
              }}
            />
          )
        }
        {
          tabValue === 'statistics' && (
            <StatisticContent
              {...{
                auth,
                selectedClient: clonedSelectedClient,
                selectedSiteId,
              }}
            />
          )
        }
      </Box>

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
export default ClientContent;